import { useState } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, X, Zap } from "lucide-react";

const TIERS = {
  starter: {
    label: "Basic",
    price: "$999",
    blurb: "Stop losing calls and missed jobs",
    features: [
      "AI Receptionist (24/7)",
      "Missed-call text-back",
      "Basic SMS + Email follow up",
      "Google Business Profile setup",
      "Review Request",
      "Calendar & CRM setup",
      "Monthly performance report",
    ],
  },
  growth: {
    label: "Plus",
    price: "$1,999",
    badge: "Most popular",
    blurb: "Turn every lead into booked jobs ad grow automatically",
    features: [
      "Everything in Basic plus",
      "Advanced AI receptionist (qualifies + books)",
      "DM Setter (IG + FB)",
      "30 day SMS + email follow up",
      "Review engine (grow google reviews)",
      "Google business optimization (posts + seo q&a)",
      "5 social posts per week",
      "1 high-converting VSL page",
      "Priority support + optimization",
    ],
  },
};

export default function PricingSheet({ trigger }) {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[640px] bg-cream text-ink p-0 border-l border-ink/10 overflow-y-auto"
        data-testid="pricing-sheet"
      >
        <div className="px-7 lg:px-9 py-8 border-b border-ink/10 flex items-center justify-between sticky top-0 bg-cream z-10">
          <div>
            <div className="mono text-xs uppercase tracking-[0.2em] text-mute">Pricing</div>
            <h2 className="display text-3xl mt-2">
              One fee. <span className="display-italic text-rust">Replaces a team.</span>
            </h2>
          </div>
          <button onClick={() => setOpen(false)} className="text-mute hover:text-ink" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="px-7 lg:px-9 py-8">
          <Tabs defaultValue="growth" className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-paper border border-ink/10 rounded-full p-1 h-auto">
              {Object.entries(TIERS).map(([k, t]) => (
                <TabsTrigger
                  key={k}
                  value={k}
                  className="rounded-full data-[state=active]:bg-ink data-[state=active]:text-cream data-[state=active]:shadow-none mono text-xs uppercase tracking-wider py-2.5"
                  data-testid={`pricing-tab-${k}`}
                >
                  {t.label}
                </TabsTrigger>
                                        ))}
            </TabsList>

            {Object.entries(TIERS).map(([k, t]) => (
              <TabsContent key={k} value={k} className="mt-8 focus:outline-none">
                {t.badge && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rust/10 text-rust mono text-[10px] uppercase tracking-wider mb-3">
                    <span className="w-1 h-1 rounded-full bg-rust"></span> {t.badge}
                  </span>
                                                        )}
                <div className="flex items-baseline gap-2">
                  <span className="display text-7xl">{t.price}</span>
                  <span className="text-ink/60 text-sm">/ month</span>
                </div>
                <p className="text-ink/75 mt-5 max-w-[44ch]">{t.blurb}</p>

                <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check size={14} className="text-rust mt-1 shrink-0" />
                      <span>{f}</span>
                    </li>
                                                            ))}
                </ul>
 {/* ── Add-On Card ── always visible below both pricing tabs ── */}
          <div className="mt-8 rounded-2xl bg-rust text-cream overflow-hidden">
            <div className="px-6 py-5 flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Zap size={14} className="text-cream/70 shrink-0" />
                  <span className="mono text-[10px] uppercase tracking-[0.18em] text-cream/70">
                    Optional Add-On
                  </span>
                </div>
                <h3 className="display text-xl leading-tight">
                  Website / Funnel Build
                </h3>
                <p className="text-cream/75 text-sm mt-1.5 max-w-[36ch]">
                  A high-converting, brand-matched website or sales funnel — built and launched for you.
                </p>
              </div>
              <div className="text-right shrink-0">
                <div className="flex items-baseline gap-1 justify-end">
                  <span className="display text-4xl">$499</span>
                </div>
                <div className="mono text-[10px] uppercase tracking-wider text-cream/65 mt-0.5">
                  One-time setup fee
                </div>
              </div>
            </div>
            <div className="border-t border-cream/20 px-6 py-3 flex items-center justify-between">
              <span className="text-cream/70 text-xs">Add to any plan at checkout</span>
              <Link
                to="/audit"
                onClick={() => setOpen(false)}
                className="mono text-[11px] uppercase tracking-wider text-cream hover:text-cream/80 transition-colors"
              >
                Add this →
              </Link>
            </div>
          </div>
                <div className="mt-9 flex flex-wrap items-center gap-3">
                  <Link
                    to="/audit"
                    onClick={() => setOpen(false)}
                    className="btn-primary px-6 py-3.5 rounded-full text-sm font-medium"
                    data-testid={`pricing-cta-${k}`}
                  >
                    Start with {t.label} →
                  </Link>
                  <Link
                    to="/roi"
                    onClick={() => setOpen(false)}
                    className="btn-ghost px-6 py-3.5 rounded-full text-sm font-medium"
                  >
                    Run my ROI numbers
                  </Link>
                </div>

                <div className="mt-10 pt-6 border-t border-ink/10 text-xs text-mute">
                  HIPAA-compliant flows · BAA available · cancel any time · 30-day ROI guarantee
                </div>
              </TabsContent>
                                      ))}
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
        );
}
